/**
 * MediaUploadGrid Component
 * Grid-based media management with upload and URL input support.
 * Displays existing media items with remove buttons and an "add more" card.
 */

import React, { useState, useRef } from 'react';
import { X, Upload, Link, Loader2, Image, Video, Plus } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

interface MediaUploadGridProps {
  /** Array of media URLs */
  mediaUrls: string[];
  /** Callback when media is added (url is the displayable URL, path is the storage path) */
  onMediaAdd: (url: string, path?: string) => void;
  /** Callback when media is removed by index */
  onMediaRemove: (index: number) => void;
  /** Maximum number of media items allowed */
  maxItems?: number;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Compact mode for smaller thumbnails (option-level) */
  compact?: boolean;
}

type AddMode = 'none' | 'url' | 'upload';

const MediaUploadGrid: React.FC<MediaUploadGridProps> = ({
  mediaUrls,
  onMediaAdd,
  onMediaRemove,
  maxItems = 5,
  disabled = false,
  compact = false
}) => {
  // Auto-show upload UI when no media exists
  const [addMode, setAddMode] = useState<AddMode>(mediaUrls.length === 0 ? 'upload' : 'none');
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canAddMore = mediaUrls.length < maxItems && !disabled;

  // Determine media type from URL
  const getMediaType = (url: string): 'image' | 'video' | 'unknown' => {
    if (!url) return 'unknown';
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be') || lowerUrl.includes('vimeo.com')) {
      return 'video';
    }
    if (/\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(url)) {
      return 'video';
    }
    if (/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url)) {
      return 'image';
    }
    // Default to image for uploaded files
    return 'image';
  };

  // Build full URL for display
  const getFullUrl = (url: string): string => {
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return `${API_URL}${url}`;
    return url;
  };

  // Handle file upload
  const handleFileUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const availableSlots = maxItems - mediaUrls.length;
    
    if (availableSlots <= 0) {
      setError(`Maximum of ${maxItems} media items reached`);
      return;
    }

    const filesToUpload = fileArray.slice(0, availableSlots);
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 15 * 1024 * 1024; // 15MB

    for (const file of filesToUpload) {
      if (!validTypes.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Please upload PNG, JPG, GIF or WEBP`);
        return;
      }
      if (file.size > maxSize) {
        setError(`File ${file.name} exceeds 15MB limit`);
        return;
      }
    }

    setUploading(true);
    setError(null);

    try {
      // Upload all files in parallel
      const uploadPromises = filesToUpload.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_URL}/api/question-images/upload`, {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || errorData.error || 'Upload failed');
        }

        return response.json();
      });

      const results = await Promise.all(uploadPromises);
      
      // Add all uploaded media URLs
      results.forEach(data => {
        onMediaAdd(data.image_url, data.image_path);
      });
      
      setAddMode('none');
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Handle URL submission
  const handleUrlSubmit = () => {
    const trimmedUrl = urlInput.trim();
    if (!trimmedUrl) {
      setError('Please enter a URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(trimmedUrl);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    onMediaAdd(trimmedUrl);
    setUrlInput('');
    setAddMode('none');
    setError(null);
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && canAddMore) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || !canAddMore) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  };

  const thumbnailSize = compact ? 'w-20 h-20' : 'w-28 h-28';
  const gridCols = compact ? 'grid-cols-4' : 'grid-cols-3 md:grid-cols-4';

  return (
    <div className="space-y-2">
      {/* Media Grid */}
      <div className={`grid ${gridCols} gap-2`}>
        {/* Existing Media Items */}
        {mediaUrls.map((url, index) => {
          const mediaType = getMediaType(url);
          const fullUrl = getFullUrl(url);

          return (
            <div
              key={index}
              className={`relative group ${thumbnailSize} rounded-lg border border-gray-200 overflow-hidden bg-gray-50`}
            >
              {mediaType === 'image' ? (
                <img
                  src={fullUrl}
                  alt={`Media ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `<div class="w-full h-full flex items-center justify-center text-gray-400"><svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg></div>`;
                  }}
                />
              ) : mediaType === 'video' ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-purple-50">
                  <Video className="w-6 h-6 text-purple-500" />
                  <span className="text-xs text-purple-600 mt-1">Video</span>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-6 h-6 text-gray-400" />
                </div>
              )}

              {/* Remove Button */}
              {!disabled && (
                <button
                  onClick={() => onMediaRemove(index)}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600"
                  title="Remove"
                >
                  <X className="w-3 h-3" />
                </button>
              )}

              {/* Index Badge */}
              <div className="absolute bottom-1 left-1 w-5 h-5 bg-black/60 text-white text-xs rounded flex items-center justify-center">
                {index + 1}
              </div>
            </div>
          );
        })}

        {/* Add More Card */}
        {canAddMore && addMode === 'none' && (
          <div
            onClick={() => setAddMode('upload')}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              ${thumbnailSize} rounded-lg border-2 border-dashed cursor-pointer
              flex flex-col items-center justify-center gap-1 transition-all
              ${isDragging 
                ? 'border-teal-500 bg-teal-50' 
                : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'
              }
              ${uploading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 text-teal-500 animate-spin" />
            ) : (
              <>
                <Plus className="w-5 h-5 text-gray-400" />
                <span className="text-xs text-gray-500">Add</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Mode UI */}
      {addMode !== 'none' && canAddMore && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">
          {/* Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => { setAddMode('upload'); setError(null); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                addMode === 'upload'
                  ? 'bg-teal-500 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
            <button
              onClick={() => { setAddMode('url'); setError(null); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                addMode === 'url'
                  ? 'bg-teal-500 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Link className="w-4 h-4" />
              URL
            </button>
          </div>

          {/* Upload Mode */}
          {addMode === 'upload' && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                className="hidden"
                multiple
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`
                  h-20 border-2 border-dashed rounded-lg cursor-pointer
                  flex flex-col items-center justify-center gap-1 transition-all
                  ${isDragging ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-400'}
                  ${uploading ? 'pointer-events-none' : ''}
                `}
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-gray-400" />
                    <p className="text-xs text-gray-500">Drop files or click to upload</p>
                    <p className="text-xs text-gray-400">PNG, JPG, GIF, WEBP (max 15MB)</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* URL Mode */}
          {addMode === 'url' && (
            <div className="flex gap-2">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                placeholder="https://example.com/image.jpg"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                autoFocus
              />
              <button
                onClick={handleUrlSubmit}
                className="px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-md hover:bg-teal-600 transition-colors"
              >
                Add
              </button>
            </div>
          )}

          {/* Cancel Button */}
          <button
            onClick={() => { setAddMode('none'); setError(null); setUrlInput(''); }}
            className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="text-xs text-red-600 mt-1">
          {error}
        </div>
      )}

      {/* Count Display */}
      {mediaUrls.length > 0 && (
        <div className="text-xs text-gray-500">
          {mediaUrls.length} of {maxItems} media items
        </div>
      )}
    </div>
  );
};

export default MediaUploadGrid;

